import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import useIdentityStore from './useIdentityStore';
import { logEvent } from '../utils/telemetry';

const TIMER_KEY = 'timer_state';

/**
 * Timer store with 4-phase support: prep → focus → reflect → rest (recharge)
 * Persists to localStorage so sessions survive page reloads
 *
 * Persist cadence (fixes ~600 Firestore writes/50min-session write amplification):
 *   - tick() persists to localStorage ONLY (storage.setLocal), every second — so a
 *     refresh mid-countdown still restores to the second via loadPersistedState()'s
 *     elapsed-time math below.
 *   - Meaningful transitions (start/pause/resume/phase-change/complete/addTime/
 *     setSessionMeta/setActiveTask) persist to BOTH localStorage and Firestore
 *     (storage.set) as before — this is the cloud copy the mini-player and other
 *     devices actually see, and it now updates only a handful of times per session
 *     instead of ~600.
 *   - timer_state's Firestore doc is scoped PER DEVICE, not per account (2026-08-30 —
 *     see storage.js's PER_DEVICE_SINGLE_DOC_KEYS / getDeviceId()): a phone and a
 *     desktop, or two tabs on different machines, used to fight over one shared
 *     countdown. This store needed NO changes for that fix — it still calls plain
 *     storage.get/set/setLocal/remove with the SAME 'timer_state' key exactly as
 *     before; the per-device routing (and the local storage key staying un-suffixed,
 *     so same-device consumers like the mini-player keep working) is entirely
 *     internal to storage.js.
 */

function loadPersistedState() {
    const saved = storage.get(TIMER_KEY, null);
    if (!saved || !saved.isRunning) return null;

    // Calculate elapsed time since last save
    const now = Date.now();
    const elapsed = Math.floor((now - saved.lastSavedAt) / 1000);

    if (!saved.isPaused) {
        saved.timeRemaining = Math.max(0, saved.timeRemaining - elapsed);
    }

    // If timer expired while away, don't restore a dead session
    if (saved.timeRemaining <= 0) {
        storage.remove(TIMER_KEY);
        return null;
    }

    delete saved.lastSavedAt;
    return saved;
}

const persisted = loadPersistedState();

const DEFAULT_STATE = {
    timeRemaining: 2 * 60, // Start with prep
    totalTime: 2 * 60,
    focusDuration: 50 * 60,
    restDuration: 10 * 60, // Default 10m recharge
    reflectDuration: 5 * 60,
    prepDuration: 2 * 60, // Default 2m prep
    phase: 'prep',
    isRunning: false,
    isPaused: false,
    sessionStartTime: null,
    // Freestyle session metadata (persisted so mini-player can show it)
    sessionMeta: null, // { identityId, projectId, taskId, tags }
    // Active Main Task — the ONE task the user is currently executing
    activeTaskId: null,
};

/** Shared snapshot shape for both the cloud-synced and local-only persist paths. */
function _timerSnapshot(s) {
    return {
        focusDuration: s.focusDuration,
        restDuration: s.restDuration,
        reflectDuration: s.reflectDuration,
        prepDuration: s.prepDuration,
        phase: s.phase,
        isRunning: s.isRunning,
        isPaused: s.isPaused,
        timeRemaining: s.timeRemaining,
        totalTime: s.totalTime,
        sessionStartTime: s.sessionStartTime,
        sessionMeta: s.sessionMeta,
        activeTaskId: s.activeTaskId,
        lastSavedAt: Date.now(),
    };
}

const useTimerStore = create((set, get) => ({
    ...(persisted || DEFAULT_STATE),

    // Persist helper — called after every MEANINGFUL state change (start/pause/
    // resume/phase-change/complete/etc.). Syncs to Firestore too.
    _persist() {
        storage.set(TIMER_KEY, _timerSnapshot(get()));
    },

    // High-frequency local-only persist — called every tick(). Never touches
    // Firestore (see the module docblock above for why); keeps localStorage's
    // lastSavedAt/timeRemaining fresh so a refresh restores to the second.
    _persistLocal() {
        storage.setLocal(TIMER_KEY, _timerSnapshot(get()));
    },

    setFocusDuration(seconds) {
        set({ focusDuration: seconds, timeRemaining: seconds, totalTime: seconds });
    },

    setRestDuration(seconds) {
        set({ restDuration: seconds });
    },

    setReflectDuration(seconds) {
        set({ reflectDuration: seconds });
    },

    setPrepDuration(seconds) {
        set({ prepDuration: seconds });
    },

    setSessionMeta(meta) {
        set({ sessionMeta: meta });
        get()._persist();
    },

    setActiveTask(taskId) {
        set({ activeTaskId: taskId });
        get()._persist();
    },

    startTimer() {
        const { prepDuration, focusDuration, sessionMeta } = get();
        const initialPhase = prepDuration > 0 ? 'prep' : 'focus';
        const initialDuration = prepDuration > 0 ? prepDuration : focusDuration;

        // Auto-set active task from sessionMeta if exactly one task is selected
        const taskIds = sessionMeta?.taskIds || (sessionMeta?.taskId ? [sessionMeta.taskId] : []);
        const autoActiveTask = taskIds.length === 1 ? taskIds[0] : null;

        set({
            isRunning: true,
            isPaused: false,
            phase: initialPhase,
            timeRemaining: initialDuration,
            totalTime: initialDuration,
            sessionStartTime: new Date().toISOString(),
            activeTaskId: autoActiveTask,
        });
        get()._persist();
    },

    pauseTimer() {
        set({ isPaused: true });
        get()._persist();
    },

    resumeTimer() {
        set({ isPaused: false });
        get()._persist();
    },

    tick(deltaSecs = 1) {
        set(state => ({
            timeRemaining: Math.max(0, state.timeRemaining - deltaSecs),
        }));
        // Local-only — see the module docblock. The cloud copy updates on the next
        // meaningful transition (pause/resume/phase-change/complete), not every tick.
        get()._persistLocal();
    },

    addTime(seconds) {
        set(state => ({
            timeRemaining: Math.max(0, state.timeRemaining + seconds),
            totalTime: Math.max(0, state.totalTime + seconds),
        }));
        get()._persist();
    },

    nextPhase() {
        const { phase, focusDuration, restDuration, reflectDuration, timeRemaining } = get();

        // Telemetry: Track if phase was skipped manually (time > 0)
        if (timeRemaining > 0) {
            logEvent('timer_phase_skipped', { phase, remainingSeconds: timeRemaining });
        }

        if (phase === 'prep') {
            set({
                phase: 'focus',
                timeRemaining: focusDuration,
                totalTime: focusDuration,
                isPaused: false,
            });
        } else if (phase === 'focus') {
            if (reflectDuration > 0) {
                set({
                    phase: 'reflect',
                    timeRemaining: reflectDuration,
                    totalTime: reflectDuration,
                    isPaused: false,
                });
            } else if (restDuration > 0) {
                set({
                    phase: 'rest',
                    timeRemaining: restDuration,
                    totalTime: restDuration,
                    isPaused: false,
                });
            } else {
                // If reflect and rest are BOTH skipped, just end
                set({ isRunning: false, isPaused: false, phase: 'prep', sessionMeta: null });
            }
        } else if (phase === 'reflect') {
            if (restDuration > 0) {
                set({
                    phase: 'rest',
                    timeRemaining: restDuration,
                    totalTime: restDuration,
                    isPaused: false,
                });
            } else {
                set({ isRunning: false, isPaused: false, phase: 'prep', sessionMeta: null });
            }
        } else {
            // End of cycle
            set({
                isRunning: false,
                isPaused: false,
                phase: 'prep',
                sessionMeta: null,
            });
        }
        get()._persist();
    },

    resetTimer() {
        const { prepDuration } = get();
        set({
            timeRemaining: prepDuration,
            totalTime: prepDuration,
            isRunning: false,
            isPaused: false,
            phase: 'prep',
            sessionStartTime: null,
            sessionMeta: null,
            activeTaskId: null,
        });
        storage.remove(TIMER_KEY);
    },

    stopTimer() {
        const { phase, timeRemaining, totalTime, sessionMeta } = get();
        
        // Telemetry: Track early abandonment if >10% time was left in focus phase
        if (phase === 'focus' && totalTime > 0 && timeRemaining > (totalTime * 0.1)) {
            const completionPercent = Math.round(((totalTime - timeRemaining) / totalTime) * 100);
            logEvent('session_ended_early', { completionPercent, activeTaskId: sessionMeta?.taskId || null });
        }

        set({
            isRunning: false,
            isPaused: false,
            sessionMeta: null,
            activeTaskId: null,
        });
        storage.remove(TIMER_KEY);
    },
}));

// Pick up mapped UID-bound data on init
onRehydrate(() => {
    const loaded = loadPersistedState();
    if (loaded) {
        useTimerStore.setState(loaded);
    } else {
        useTimerStore.setState({ isRunning: false, phase: 'prep' });
    }
});

export default useTimerStore;
